import { DestroyRef, inject, WritableSignal } from '@angular/core';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';
import { DateTransformationService } from '../../services/transformation-helper/date-transformation.service';
import {
  DisplayColumn,
  DynamicTableType,
  IDAndTypeInterface,
  ListColumn,
  OrderOption,
  SbCompaniesListColumn,
  SbDeviceListColumn,
  SbLocationListColumn,
  SbProductListColumn,
} from '../../services/global/global-interfaces';
import {
  AddressDTO,
  AttachmentCountDTO,
  AttributeDTO,
  CompanyDTO,
  CompanyFullDTO,
  DeviceDTO,
  DeviceFullDTO,
  IdentityDTO,
  LocationDTO,
  LocationFullDTO,
  Order,
  PointDTO,
} from '../../services/api/api';
import { DateTime } from 'luxon';
import { SbDateTime, SbLocalDateTime } from '../../services/api/api-date-time';
import { basePaths } from './BasePaths';
import { getJSONFromMap, getMapFromJSON } from '../../services/global/global-methods';
import { QueryParamService } from '../../services/lists/query-params';
import { TranslateService } from '@ngx-translate/core';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { UserService } from '../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColumnOptionComponent } from '../identity-ordering/column-option.component';
import { ModalService } from '../../services/gui/modal/modal.service';
import { CdkDragDrop, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { FreetextResult } from '../../services/gui/modal/modal.model';
import { ModalNavigationComponent } from '../../services/gui/modal/modal-navigation-component';
import { RequestClass } from '../../services/global/RequestClass';

type ColumnFilterValue<T> = { column: T; value: string };

export abstract class DynamicTableBase<U extends SbDeviceListColumn | SbLocationListColumn | SbCompaniesListColumn | SbProductListColumn>
  extends RequestClass
  implements ModalNavigationComponent
{
  filterPanelRef: FilterPanelComponent;

  isInModalContext: boolean = false;

  protected readonly stringTransformation = inject(StringTransformationService);
  protected readonly dateTransformation = inject(DateTransformationService);
  protected readonly queryParamService = inject(QueryParamService);
  protected readonly translate = inject(TranslateService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly userService = inject(UserService);
  protected readonly modalService = inject(ModalService);

  /* columns variables */
  previousIndex: number = -1;
  protected columns: U[] = [];
  protected selectedColumns: U[] = [];
  protected columnDefaults: U[] = [];

  /* Column Filtering */
  removableColumnFilters: string[] = [];
  isResetting: boolean = false;
  protected columnFiltersMap: Map<string, ColumnFilterValue<U>> = new Map();

  // Filter config
  protected filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  createTableCell(column: ListColumn, objectType: DynamicTableType, dataObject: any): DisplayColumn {
    const calculatedRawValue = this.getRawValue(column, objectType, dataObject);
    return {
      ...column,
      rawValue: calculatedRawValue,
      listRowValue: this.listRowValueToString(calculatedRawValue),
      isValBoolean: typeof calculatedRawValue === 'boolean',
    };
  }

  private getRawValue(column: ListColumn, objectType: DynamicTableType, dataObject: any) {
    let value = null;
    const path = basePaths[objectType][column.type];
    switch (column.type) {
      case 'PROPERTY':
        return this.resolveColumnRawValue(column.id, dataObject);

      case 'DEVICE_ATTRIBUTE':
      case 'LOCATION_ATTRIBUTE':
      case 'COMPANY_ATTRIBUTE':
      case 'PRODUCT_ATTRIBUTE':
        if (path) {
          value = path.split('.').reduce((current, propertyName) => {
            return current && typeof current === 'object' && propertyName in current ? current[propertyName] : null;
          }, dataObject);
        } else {
          value = dataObject;
        }
        return value?.attributes ? this.getAttributeValue(value.attributes, column.title) : null;
      default:
        return '? ? ? ? ?';
    }
  }

  resolveColumnRawValue<T>(columnId: string, object: T) {
    if (!columnId || !object) {
      return null;
    }
    const propertyNames: string[] = columnId.split('.');
    const value = propertyNames.reduce((current, propertyName) => {
      if (current == null) {
        return null;
      }

      if (Array.isArray(current)) {
        return this.resolveColumnRawValueArray(current, propertyName);
      }
      return typeof current === 'object' && propertyName in current ? current[propertyName] : null;
    }, object);

    if (typeof value === 'object' && value instanceof AttachmentCountDTO) {
      return value.attachmentCount ?? null;
    }
    return value;
  }

  private resolveColumnRawValueArray<T, K extends keyof T>(array: T[], key: K): string[] {
    return array.map(obj => {
      const val = obj?.[key];

      if (val == null) {
        return 'n. def.';
      }
      if (typeof val === 'object' && val.hasOwnProperty('name')) {
        return val['name'];
      }
      try {
        return JSON.stringify(val);
      } catch (e) {
        return '[unserializable]';
      }
    });
  }

  getAttributeValue<T extends AttributeDTO>(attributes: T[], attributeTitle: string): string | null {
    if (!attributes) {
      return null;
    }
    return attributes.find(attr => attr.name === attributeTitle)?.value ?? null;
  }

  listRowValueToString(e: any) {
    // null check
    if (e === undefined || e === null) {
      return 'n. def.';
    }
    // primitive check
    if (typeof e === 'number') {
      return e.toString();
    }
    if (typeof e === 'boolean') {
      return e.toString();
    }
    if (typeof e === 'string') {
      return this.processString(e);
    }
    // explicit DTO / Object check
    if (e instanceof AddressDTO) {
      return this.stringTransformation.addressToString(e);
    }

    if (e instanceof PointDTO && e.data?.coordinates[0] && e.data.coordinates[1]) {
      // for currentLocation property
      return e.data.coordinates[1].toFixed(6) + ', ' + e.data.coordinates[0].toFixed(6);
    }

    if (e.type === 'Point') {
      // for position property of AddressDTO
      return e.coordinates[1].toFixed(6) + ', ' + e.coordinates[0].toFixed(6);
    }

    if (e instanceof DeviceDTO || e instanceof DeviceFullDTO) {
      return e.id;
    }

    if (e instanceof CompanyDTO || e instanceof CompanyFullDTO || e instanceof LocationDTO || e instanceof LocationFullDTO) {
      return this.stringTransformation.locationOrCompanyToShortWithId(e);
    }

    // Array check
    if (Array.isArray(e)) {
      // e.g. Screens in Devices
      return this.processArray(e);
    }

    // Date checks
    if (e instanceof DateTime) {
      return this.dateTransformation.dateToDateTimeString(e);
    }

    if (e instanceof SbDateTime || e instanceof SbLocalDateTime) {
      return e.value ? this.dateTransformation.dateToDateTimeString(e.value) : '';
    }

    // property checks
    if (e.name) {
      return e.name;
    }
    if (e.id) {
      return e.id?.toString();
    }
    // default return whole object
    return e;
  }

  private processArray(array: any[]): string {
    return (
      array
        .map(item => {
          if (item == null) {
            return 'n. def.';
          } else if (typeof item === 'object' && item?.name) {
            return item?.name;
          } else if (typeof item === 'object' && item?.id) {
            return item?.id;
          } else {
            return item.toString();
          }
        })
        .join(', ') ?? null
    );
  }

  private processString(value: string) {
    if (value) {
      return value;
    }
    return 'n. def.';
  }

  identity(col: U): U {
    return col;
  }

  getObjectId<T extends IdentityDTO>(object: T): string | null {
    if (!object) {
      return null;
    }
    return JSON.stringify([object.id]);
  }

  // columns

  openColumnOptionModal(callback: () => void) {
    // show selection dialog
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: ColumnOptionComponent,
          data: { columns: this.columns, selectedColumns: this.selectedColumns, defaultColumns: this.columnDefaults },
        },
        title: this.translate.instant('lang.globals.columnOptions'),
        modalOptions: { size: 'xl' },
      })
      .then((result: ListColumn[]) => {
        if (result?.length >= 0) {
          this.selectedColumns = result as U[];
        }
      })
      .catch(() => {})
      .finally(() => {
        callback();
      });
  }

  saveSelectedColumns(settingName: string): void {
    const uniqueColumns = new Map<string, IDAndTypeInterface>();

    for (const column of this.selectedColumns) {
      if (!uniqueColumns.has(column.id)) {
        uniqueColumns.set(column.id, { id: column.id, type: column.type });
      }
    }
    this.userService.setUserClientSetting(settingName, JSON.stringify(Array.from(uniqueColumns.values()))).then();
  }

  async loadSelectedColumns(settingName: string): Promise<any> {
    const e = await this.userService.getUserClientSetting(settingName);
    if (e && e !== '[]') {
      try {
        const cols = JSON.parse(e) as IDAndTypeInterface[];
        // remove potential duplicates
        const finalCols: IDAndTypeInterface[] = [];
        for (const col of cols) {
          if (!finalCols.some(value => value.id === col.id)) {
            finalCols.push(col);
          }
        }
        return Promise.resolve(finalCols);
      } catch (ex) {
        console.error(ex);
        return Promise.resolve(null);
      }
    } else {
      return Promise.resolve(null);
    }
  }

  // column filter
  isFilterEnabled(): boolean {
    return this.columnFiltersMap && this.columnFiltersMap.size > 0;
  }

  refreshColumnFilters() {
    Array.from(this.columnFiltersMap.keys())
      .filter(key => !this.selectedColumns.some(selectedColumn => selectedColumn.id === key)) // filter keys that are not selected
      .forEach(key => this.columnFiltersMap.delete(key)); // remove those not selected filtering from Map
  }

  initColumnFilterMap(queryParam: string) {
    const setting = this.queryParamService.getQueryParam(queryParam);
    this.columnFiltersMap = new Map();
    if (!setting) {
      return;
    }
    try {
      const columnFilter = JSON.parse(setting, getMapFromJSON) ?? new Map<string, string>();
      if (columnFilter.size <= 0) {
        return;
      }
      this.columns
        .filter(searchColumn => columnFilter.has(searchColumn.id))
        .forEach(pColumn => {
          this.columnFiltersMap.set(pColumn.id, { column: pColumn, value: columnFilter.get(pColumn.id) });
        });
    } catch (e) {
      console.log('Error in deviceFilter parsed usersettings', e);
      this.columnFiltersMap = new Map<string, ColumnFilterValue<U>>();
    }
    this.addColumnFiltersToFilterPanel();
  }

  private addColumnFiltersToFilterPanel() {
    if (!this.filterPanelRef) {
      return;
    }
    Array.from(this.columnFiltersMap.entries())
      .map(([colId, columnValue]) => {
        return {
          filterID: colId,
          value: columnValue,
          displayValue: this.translate.instant(columnValue.column.title) + ': ' + columnValue.value,
          icon: 'funnelFilled',
          unselectableByTag: true,
        };
      })
      .forEach(value => this.filterPanelRef.valueSelected(value));
  }

  protected saveColumnFilterToQueryParam(queryParam: string) {
    if (!this.columnFiltersMap || this.columnFiltersMap.size === 0) {
      this.queryParamService.clearQueryParam(queryParam);
      return;
    }

    const qpMap = new Map(Array.from(this.columnFiltersMap.entries()).map(([key, value]) => [key, value.value]));
    if (qpMap.size === 0) {
      this.queryParamService.clearQueryParam(queryParam);
    } else {
      const filterObjectString = JSON.stringify(qpMap, getJSONFromMap);
      this.queryParamService.setQueryParam(queryParam, filterObjectString);
    }
  }

  protected removeColumnFilter(filterId: string) {
    if (this.filterPanelRef) {
      this.columnFiltersMap.delete(filterId);
      this.filterPanelRef.valueUnselected(filterId);
    }
  }

  isFilteringAllowed(column: U) {
    if (!column) {
      return false;
    }
    return (
      column.type === 'DEVICE_ATTRIBUTE' ||
      column.type === 'LOCATION_ATTRIBUTE' ||
      column.type === 'COMPANY_ATTRIBUTE' ||
      column.type === 'PRODUCT_ATTRIBUTE' ||
      (column.type === 'PROPERTY' && column.canFilter)
    );
  }

  isSortingAllowed(column: U) {
    if (!column) {
      return false;
    }
    return (
      column.type === 'DEVICE_ATTRIBUTE' ||
      column.type === 'LOCATION_ATTRIBUTE' ||
      column.type === 'COMPANY_ATTRIBUTE' ||
      column.type === 'PRODUCT_ATTRIBUTE' ||
      (column.type === 'PROPERTY' && column.canOrder && column.orderId != null)
    );
  }

  protected refreshAllOrderOptions() {
    return this.selectedColumns
      .filter(column => this.isSortingAllowed(column))
      .map(column => {
        return { value: column, direction: Order.asc, label: this.translate.instant(column.title) };
      });
  }

  protected setToOrderFilterPanel(filterPanelOrderOption: WritableSignal<OrderOption<U>>, pValue: U) {
    filterPanelOrderOption.update(orderOption => {
      orderOption.value = pValue;
      return { ...orderOption };
    });
    this.setFilterPanelHasChanged();
  }

  protected setOrderDirectionFilterPanel(filterPanelOrderOption: WritableSignal<OrderOption<U>>, value: Order) {
    filterPanelOrderOption.update(orderOption => {
      orderOption.direction = value;
      return { ...orderOption };
    });
    this.setFilterPanelHasChanged();
  }

  // filterpanel
  protected setFilterPanelHasChanged() {
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  protected checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  protected openFilterPanel(element: HTMLDivElement) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(element, false);
    }
  }

  protected scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  protected initTagSubscription(): void {
    if (this.filterPanelRef) {
      this.filterPanelRef.tagListObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: event => {
          if (event.selected && !this.isResetting) {
            this.isResetting = true;
            if (this.removableColumnFilters.length > 0) {
              this.removableColumnFilters.forEach(filterID => {
                const columnValue = this.columnFiltersMap.get(filterID);
                this.filterPanelRef.valueSelected({
                  filterID,
                  value: columnValue,
                  displayValue: this.translate.instant(columnValue.column.title) + ': ' + columnValue.value,
                  icon: 'funnelFilled',
                  unselectableByTag: true,
                });
              });
              this.removableColumnFilters = [];
              this.filterPanelRef.hasExternalChanges.set(false);
            }
          } else {
            if (!event.selected && this.isResetting) {
              this.isResetting = false;
            }
            if (this.columnFiltersMap.has(event.filterID)) {
              this.removableColumnFilters.push(event.filterID);
              // this.removeColumnFilter(event.filterID);
              this.filterPanelRef.valueUnselected(event.filterID);
              this.setFilterPanelHasChanged();
            }
          }
        },
      });
    }
  }

  // Layout methods
  getColumnIcon(column: U) {
    switch (column.type) {
      case 'PROPERTY':
        return 'wrench';
      case 'DEVICE_ATTRIBUTE':
        return 'playerOnline';
      case 'LOCATION_ATTRIBUTE':
        return 'location';
      case 'COMPANY_ATTRIBUTE':
        return 'company';
      case 'PRODUCT_ATTRIBUTE':
        return 'screenPlay';
      default:
        return 'questionmark';
    }
  }

  getColumnTypeTitle(column: U): string {
    switch (column.type) {
      case 'PROPERTY':
        return 'Property';
      case 'DEVICE_ATTRIBUTE':
        return 'Device Attribute';
      case 'LOCATION_ATTRIBUTE':
        return 'Location Attribute';
      case 'COMPANY_ATTRIBUTE':
        return 'Company Attribute';
      case 'PRODUCT_ATTRIBUTE':
        return 'Product Attribute';
      default:
        return '';
    }
  }

  // drag methods

  dragStarted(event: CdkDragStart, index: number) {
    this.previousIndex = index;
  }

  dropListDropped(event: CdkDragDrop<any>, callback: () => void) {
    if (event) {
      moveItemInArray(this.selectedColumns, event.previousIndex, event.currentIndex);
      this.selectedColumns = [...this.selectedColumns];
      callback();
    }
  }

  // column filtering
  columnFilter(pColumn: U, queryParamName: string) {
    let initial: string | undefined = this.columnFiltersMap?.get(pColumn.id)?.value ?? undefined;
    if (!this.stringTransformation.stringNotNull(initial)) {
      initial = undefined;
    }

    this.modalService
      .freetextFiltered({
        attributeType: pColumn.type,
        datatype: pColumn.id,
        initial,
        title: this.translate.instant('lang.globals.selectValueForNamedFilter', {
          name: pColumn.title ?? pColumn.id,
        }),
      })
      .then((e: FreetextResult<string>) => {
        if (e.cancel) {
          return;
        }

        this.loading.set(true);

        this.removeColumnFilter(pColumn.id); // always remove filter to avoid double filter

        if (e.empty || e.value?.length > 0) {
          const columnSearchValue = e.empty ? '' : e.value;
          this.columnFiltersMap.set(pColumn.id, { column: pColumn, value: columnSearchValue });
          this.filterPanelRef.valueSelected({
            filterID: pColumn.id,
            value: { column: pColumn, value: columnSearchValue },
            displayValue: this.translate.instant(pColumn.title) + ': ' + columnSearchValue,
            icon: 'funnelFilled',
            unselectableByTag: true,
          });
        }
        // else remove Column filter, but since we remove before adding there is no need for else part
        if (!this.isInModalContext) {
          this.saveColumnFilterToQueryParam(queryParamName);
        }
        this.requestDebounced();
      })
      .catch(() => {});
  }
}
